import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import {
  LayoutDashboard, Upload, Calendar, MessageSquare, CreditCard, User,
  CheckCircle, Clock, XCircle, FileText, Trash2, Download, Send,
  Loader, AlertCircle, Plus, ArrowRight, LogOut, ChevronDown, Bell, Shield
} from 'lucide-react';
import axios from 'axios';

const ClientPortal = () => {
  usePageTitle('Client Portal');
  const { user, token, getAuthHeaders, logout } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  
  // Notification badges state
  const [badges, setBadges] = useState({ unreadMessages: 0, unpaidInvoices: 0, pendingDocs: 0 });

  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const headers = getAuthHeaders();

  // Fetch notification counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [messagesRes, invoicesRes, docsRes] = await Promise.all([
          axios.get(`${API}/messages`, { headers }),
          axios.get(`${API}/invoices`, { headers }),
          axios.get(`${API}/documents`, { headers })
        ]);
        
        const messages = messagesRes.data?.messages || messagesRes.data || [];
        const unreadMessages = messages.filter(m => !m.is_read && m.sender_id !== user?.id).length;
        
        const invoices = invoicesRes.data?.invoices || invoicesRes.data || [];
        const unpaidInvoices = invoices.filter(inv => inv.status !== 'paid').length;
        
        const docs = docsRes.data?.documents || docsRes.data || [];
        const pendingDocs = docs.filter(d => d.status === 'request_pending').length;
        
        setBadges({ unreadMessages, unpaidInvoices, pendingDocs });
      } catch (err) {
        console.error('Failed to fetch badges:', err);
      }
    };
    if (token) fetchBadges();
  }, [API, token, user?.id]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: 0 },
    { id: 'documents', label: 'Documents', icon: FileText, badge: badges.pendingDocs },
    { id: 'appointments', label: 'Appointments', icon: Calendar, badge: 0 },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: badges.unreadMessages },
    { id: 'payments', label: 'Invoices', icon: CreditCard, badge: badges.unpaidInvoices },
    { id: 'profile', label: 'Profile', icon: User, badge: 0 },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Portal Header */}
      <div style={{ background: '#0B1F3A' }} className="px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Clickable Logo */}
            <a 
              href="https://mataxpreparerllc.com" 
              className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-opacity"
              data-testid="portal-logo-link"
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#D4AF37' }}>
                <Shield size={20} className="text-[#0B1F3A]" />
              </div>
              <div className="hidden sm:block">
                <div className="font-heading font-bold text-white text-sm leading-none">MaTaxPreparer</div>
                <div className="text-[10px] text-[#D4AF37] tracking-wide">LLC</div>
              </div>
            </a>
            <div className="hidden sm:block h-8 w-px bg-white/20"></div>
            <div>
              <h1 className="font-heading font-bold text-white text-base sm:text-lg">Client Portal</h1>
              <p className="text-blue-300 text-xs sm:text-sm">Welcome back, {user?.first_name}!</p>
            </div>
          </div>
          
          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0B1F3A] font-bold text-sm">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="text-white text-sm hidden sm:block">{user?.first_name}</span>
                <ChevronDown size={16} className={`text-white/70 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-fadeIn">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="font-semibold text-[#0B1F3A]">{user?.first_name} {user?.last_name}</div>
                    <div className="text-sm text-[#475569] truncate">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setActiveTab('profile'); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-[#475569] hover:bg-[#F8FAFC] transition-colors"
                  >
                    <User size={16} />
                    <span className="text-sm">Profile Settings</span>
                  </button>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="logout-dropdown-btn"
                    >
                      <LogOut size={16} />
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto gap-1 py-3">
            {tabs.map(({ id, label, icon: Icon, badge }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`relative flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-[#0B1F3A] text-white'
                    : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0B1F3A]'
                }`}
                data-testid={`portal-tab-${id}`}
              >
                <Icon size={16} />
                {label}
                {badge > 0 && (
                  <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-xs font-bold ${
                    activeTab === id ? 'bg-[#D4AF37] text-[#0B1F3A]' : 'bg-red-500 text-white'
                  }`}>
                    {badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && <DashboardTab user={user} API={API} headers={headers} setActiveTab={setActiveTab} />}
        {activeTab === 'documents' && <DocumentsTab API={API} headers={headers} />}
        {activeTab === 'appointments' && <AppointmentsTab API={API} headers={headers} />}
        {activeTab === 'messages' && <MessagesTab API={API} headers={headers} user={user} />}
        {activeTab === 'payments' && <PaymentsTab API={API} headers={headers} user={user} location={location} />}
        {activeTab === 'profile' && <ProfileTab user={user} API={API} headers={headers} logout={logout} />}
      </div>
    </div>
  );
};

// ─── Dashboard Tab ────────────────────────────────────────────
const DashboardTab = ({ user, API, headers, setActiveTab }) => {
  const [stats, setStats] = useState({ appointments: [], documents: [], quotes: [] });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/appointments`, { headers }),
      axios.get(`${API}/documents`, { headers }),
      axios.get(`${API}/quotes`, { headers }),
    ]).then(([appts, docs, quotes]) => {
      // Handle both old format (array) and new format ({documents: [], total: ...})
      const appointmentsData = Array.isArray(appts.data) ? appts.data : (appts.data?.appointments || []);
      const documentsData = Array.isArray(docs.data) ? docs.data : (docs.data?.documents || []);
      const quotesData = Array.isArray(quotes.data) ? quotes.data : (quotes.data?.quotes || []);
      
      setStats({
        appointments: appointmentsData,
        documents: documentsData,
        quotes: quotesData
      });
    }).catch(err => {
      console.error('Failed to load dashboard stats:', err);
    });
  }, [API, headers]);

  const upcomingAppts = stats.appointments.filter(a => a.status !== 'cancelled').slice(0, 3);
  const pendingQuotes = stats.quotes.filter(q => q.status === 'sent');

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Welcome, {user?.first_name}!</h2>
        <p className="text-[#475569] text-sm">Here's your tax filing overview</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Documents Uploaded', value: stats.documents.length, icon: FileText, color: '#0B1F3A', tab: 'documents' },
          { label: 'Appointments', value: stats.appointments.length, icon: Calendar, color: '#2563EB', tab: 'appointments' },
          { label: 'Pending Invoices', value: pendingQuotes.length, icon: CreditCard, color: '#D4AF37', tab: 'payments' },
          { label: 'Messages', value: 'View', icon: MessageSquare, color: '#10B981', tab: 'messages' },
        ].map(({ label, value, icon: Icon, color, tab }) => (
          <button 
            key={label} 
            onClick={() => setActiveTab(tab)}
            className="bg-white rounded-2xl p-5 border border-gray-100 shadow-card hover:shadow-lg hover:border-[#0B1F3A]/30 transition-all duration-200 cursor-pointer text-left group"
            data-testid={`dashboard-tile-${tab}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
                <Icon size={18} style={{ color }} />
              </div>
              <ArrowRight size={14} className="text-gray-300 group-hover:text-[#0B1F3A] group-hover:translate-x-1 transition-all" />
            </div>
            <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wide mb-1">{label}</div>
            <div className="font-heading font-bold text-2xl text-[#0B1F3A]">{value}</div>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
        <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Upload Document', icon: Upload, tab: 'documents' },
            { label: 'Book Appointment', icon: Calendar, tab: 'appointments', isLink: true, to: '/book-appointment' },
            { label: 'Send Message', icon: MessageSquare, tab: 'messages' },
            { label: 'View Invoices', icon: CreditCard, tab: 'payments' },
          ].map(({ label, icon: Icon, tab, isLink, to }) =>
            isLink ? (
              <Link key={label} to={to}>
                <button className="w-full flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 hover:border-[#0B1F3A] hover:bg-[#F8FAFC] transition-all duration-200">
                  <Icon size={22} style={{ color: '#0B1F3A' }} />
                  <span className="text-xs font-semibold text-[#0B1F3A] text-center">{label}</span>
                </button>
              </Link>
            ) : (
              <button key={label} onClick={() => setActiveTab(tab)} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 hover:border-[#0B1F3A] hover:bg-[#F8FAFC] transition-all duration-200" data-testid={`quick-action-${tab}`}>
                <Icon size={22} style={{ color: '#0B1F3A' }} />
                <span className="text-xs font-semibold text-[#0B1F3A] text-center">{label}</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Pending Invoices Alert */}
      {pendingQuotes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-center gap-4">
          <AlertCircle size={24} className="text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-800">You have {pendingQuotes.length} pending invoice(s)</div>
            <div className="text-amber-700 text-sm">Total: ${pendingQuotes.reduce((s, q) => s + q.total, 0).toFixed(2)}</div>
          </div>
          <button onClick={() => setActiveTab('payments')} className="flex items-center gap-1 text-amber-800 font-semibold text-sm hover:underline">
            Pay Now <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Documents Tab ────────────────────────────────────────────
const DocumentsTab = ({ API, headers }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [notes, setNotes] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    fetchDocs();
    axios.get(`${API}/documents/categories`).then(r => setCategories(r.data.categories || []));
  }, []);

  const fetchDocs = async () => {
    const res = await axios.get(`${API}/documents`, { headers });
    // Handle both old format (array) and new format ({documents: [], total: ...})
    const docsData = Array.isArray(res.data) ? res.data : (res.data?.documents || []);
    setDocs(docsData);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !category) return alert('Please select a category first.');
    if (file.size > 5 * 1024 * 1024) return alert('File must be under 5MB');

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        await axios.post(`${API}/documents/upload`, {
          filename: file.name,
          category,
          file_data: ev.target.result,
          file_type: file.type,
          file_size: file.size,
          notes
        }, { headers });
        await fetchDocs();
        setNotes('');
        fileRef.current.value = '';
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await axios.delete(`${API}/documents/${docId}`, { headers });
    fetchDocs();
  };

  const handleDownload = async (doc) => {
    const res = await axios.get(`${API}/documents/${doc.id}/download`, { headers });
    const link = document.createElement('a');
    link.href = res.data.file_data;
    link.download = res.data.filename;
    link.click();
  };

  const statusBadge = (s) => {
    const map = { 'pending_review': ['bg-yellow-100 text-yellow-700', 'Pending Review'], 'reviewed': ['bg-green-100 text-green-700', 'Reviewed'] };
    const [cls, label] = map[s] || ['bg-gray-100 text-gray-600', s];
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">My Documents</h2>

      {/* Upload Section */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
        <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Upload New Document</h3>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
            data-testid="document-category-select"
          >
            <option value="">Select category *</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
          />
          <div
            onClick={() => category && fileRef.current.click()}
            className={`h-11 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
              category ? 'border-[#0B1F3A] hover:bg-[#0B1F3A]/5 text-[#0B1F3A]' : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            data-testid="document-upload-area"
          >
            {uploading ? <Loader size={18} className="animate-spin" /> : <Upload size={18} />}
            <span className="text-sm font-medium">{uploading ? 'Uploading...' : 'Choose File (max 5MB)'}</span>
          </div>
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="document-file-input" />
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {docs.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p>No documents uploaded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors duration-150" data-testid="document-row">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#0B1F3A10' }}>
                    <FileText size={18} style={{ color: '#0B1F3A' }} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-[#0B1F3A] text-sm truncate">{doc.original_filename}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#94A3B8]">{doc.category}</span>
                      {statusBadge(doc.status)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => handleDownload(doc)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Download" data-testid="download-doc-btn">
                    <Download size={16} style={{ color: '#475569' }} />
                  </button>
                  <button onClick={() => handleDelete(doc.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-testid="delete-doc-btn">
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Appointments Tab ────────────────────────────────────────────
const AppointmentsTab = ({ API, headers }) => {
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    axios.get(`${API}/appointments`, { headers }).then(r => {
      const data = Array.isArray(r.data) ? r.data : (r.data?.appointments || []);
      setAppointments(data);
    }).catch(err => console.error('Failed to load appointments:', err));
  }, [API, headers]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    await axios.put(`${API}/appointments/${id}`, { status: 'cancelled' }, { headers });
    const res = await axios.get(`${API}/appointments`, { headers });
    const data = Array.isArray(res.data) ? res.data : (res.data?.appointments || []);
    setAppointments(data);
  };

  const statusBadge = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', completed: 'bg-gray-100 text-gray-600' };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[s] || 'bg-gray-100'}`}>{s}</span>;
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">My Appointments</h2>
        <Link to="/book-appointment">
          <button className="btn-primary text-sm px-5 py-2.5"><Plus size={16} /> Book New</button>
        </Link>
      </div>
      {appointments.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-card text-[#94A3B8]">
          <Calendar size={40} className="mx-auto mb-3 opacity-30" />
          <p>No appointments scheduled</p>
          <Link to="/book-appointment"><button className="mt-4 btn-primary text-sm px-6 py-2.5">Book Appointment</button></Link>
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((appt) => (
            <div key={appt.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-card flex items-center justify-between" data-testid="appointment-row">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#0B1F3A10' }}>
                  <Calendar size={22} style={{ color: '#0B1F3A' }} />
                </div>
                <div>
                  <div className="font-semibold text-[#0B1F3A]">{appt.service_type}</div>
                  <div className="text-[#475569] text-sm">{appt.date} at {appt.time} · {appt.is_online ? 'Online' : 'In-Person'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(appt.status)}
                {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                  <button onClick={() => handleCancel(appt.id)} className="text-xs text-red-400 hover:underline" data-testid="cancel-appt-btn">Cancel</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Messages Tab ────────────────────────────────────────────
const MessagesTab = ({ API, headers, user }) => {
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages`, { headers });
      const data = Array.isArray(res.data) ? res.data : (res.data?.messages || []);
      setMessages(data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/messages`, form, { headers });
      setForm({ subject: '', body: '' });
      fetchMessages();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Secure Messages</h2>

      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-card">
        <h3 className="font-heading font-semibold text-[#0B1F3A] mb-4">Send a Message</h3>
        <form onSubmit={handleSend} className="space-y-4">
          <input value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} required placeholder="Subject" className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="message-subject-input" />
          <textarea value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} required rows={4} placeholder="Your message..." className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] resize-none transition-all" data-testid="message-body-input" />
          <button type="submit" disabled={sending} className="btn-primary px-6 py-2.5 text-sm disabled:opacity-60" data-testid="send-message-btn">
            {sending ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>

      <div className="space-y-3">
        {messages.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-card text-[#94A3B8]">
            <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
            <p>No messages yet</p>
          </div>
        ) : messages.map((msg) => (
          <div key={msg.id} className={`bg-white rounded-2xl p-5 border shadow-card ${msg.sender_id === user?.id ? 'border-blue-100' : 'border-gray-100'}`} data-testid="message-row">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-[#0B1F3A] text-sm">{msg.subject}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${msg.sender_id === user?.id ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-600'}`}>
                {msg.sender_id === user?.id ? 'You' : 'MaTaxPreparer'}
              </span>
            </div>
            <p className="text-[#475569] text-sm">{msg.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Payments Tab ────────────────────────────────────────────
const PaymentsTab = ({ API, headers, user, location }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState('');
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    fetchData();
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const payment = params.get('payment');
    const invoiceId = params.get('invoice_id');

    if (payment === 'success' && invoiceId) {
      pollInvoicePaymentStatus(invoiceId);
    } else if (payment === 'cancelled') {
      setPaymentMsg('Payment cancelled. You can try again when ready.');
    }
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/invoices`, { headers });
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  };

  const pollInvoicePaymentStatus = async (invoiceId, attempts = 0) => {
    if (attempts >= 8) { 
      setPaymentMsg('Payment verification taking longer than expected. Please refresh the page.'); 
      return; 
    }
    try {
      const res = await axios.get(`${API}/invoices/${invoiceId}/payment-status`, { headers });
      if (res.data.status === 'paid') {
        setPaymentMsg('✓ Payment successful! Thank you. Your receipt has been sent to your email.');
        fetchData();
      } else {
        setTimeout(() => pollInvoicePaymentStatus(invoiceId, attempts + 1), 2500);
      }
    } catch { 
      setPaymentMsg('Could not verify payment. Please contact support if charged.'); 
    }
  };

  const handlePay = async (invoiceId) => {
    setPayingId(invoiceId);
    try {
      const res = await axios.post(`${API}/invoices/${invoiceId}/pay`, {}, { headers });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setPaymentMsg(err.response?.data?.detail || 'Payment error. Please try again or contact support.');
      setPayingId(null);
    }
  };

  const statusBadge = (status) => {
    const map = {
      draft: ['bg-gray-100 text-gray-600', 'Draft'],
      sent: ['bg-yellow-100 text-yellow-700', 'Pending'],
      paid: ['bg-green-100 text-green-700', 'Paid'],
      overdue: ['bg-red-100 text-red-700', 'Overdue'],
      cancelled: ['bg-gray-100 text-gray-500', 'Cancelled'],
    };
    const [cls, label] = map[status] || ['bg-gray-100 text-gray-600', status];
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
  };

  const pendingInvoices = invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue');
  const paidInvoices = invoices.filter(inv => inv.status === 'paid');

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Invoices & Payments</h2>
        <p className="text-[#475569] text-sm">View and pay your invoices securely</p>
      </div>

      {paymentMsg && (
        <div className={`p-4 rounded-xl border ${paymentMsg.includes('success') || paymentMsg.includes('✓') ? 'bg-green-50 border-green-200 text-green-700' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`} data-testid="payment-status-msg">
          {paymentMsg}
        </div>
      )}

      {/* Pending Invoices */}
      {pendingInvoices.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <AlertCircle size={18} className="text-yellow-500" />
            <h3 className="font-heading font-semibold text-[#0B1F3A]">Payment Required ({pendingInvoices.length})</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingInvoices.map((invoice) => (
              <div key={invoice.id} className="p-5" data-testid="pending-invoice-row">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-semibold text-[#0B1F3A] flex items-center gap-2">
                      {invoice.invoice_number}
                      {statusBadge(invoice.status)}
                    </div>
                    <div className="text-[#475569] text-sm mt-1">{invoice.invoice_type}</div>
                    {invoice.due_date && (
                      <div className="text-[#94A3B8] text-xs mt-1">Due: {invoice.due_date}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-2xl text-[#0B1F3A]">${invoice.total?.toFixed(2)}</div>
                    <div className="text-xs text-[#94A3B8]">USD</div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="bg-[#F8FAFC] rounded-xl p-4 mb-4">
                  <div className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider mb-2">Details</div>
                  <div className="space-y-2">
                    {invoice.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-[#475569]">{item.description}</span>
                        <span className="font-semibold text-[#0B1F3A]">${item.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {invoice.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-[#64748B]">
                      <strong>Note:</strong> {invoice.notes}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handlePay(invoice.id)}
                  disabled={payingId === invoice.id}
                  className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-60"
                  style={{ background: '#D4AF37', color: '#0B1F3A' }}
                  data-testid="pay-invoice-btn"
                >
                  {payingId === invoice.id ? (
                    <><Loader size={16} className="animate-spin" /> Processing...</>
                  ) : (
                    <><CreditCard size={16} /> Pay ${invoice.total?.toFixed(2)} Now</>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Invoices History */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-heading font-semibold text-[#0B1F3A]">Invoice History</h3>
        </div>
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-[#94A3B8]">
            <CreditCard size={40} className="mx-auto mb-3 opacity-30" />
            <p>No invoices yet</p>
            <p className="text-xs mt-2">Invoices from your tax preparer will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-5" data-testid="invoice-history-row">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${invoice.status === 'paid' ? 'bg-green-50' : 'bg-gray-50'}`}>
                    {invoice.status === 'paid' ? (
                      <CheckCircle size={20} className="text-green-500" />
                    ) : (
                      <FileText size={20} className="text-gray-400" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B1F3A]">{invoice.invoice_number}</div>
                    <div className="text-[#475569] text-sm">{invoice.invoice_type}</div>
                    <div className="text-[#94A3B8] text-xs">
                      {invoice.status === 'paid' 
                        ? `Paid on ${new Date(invoice.paid_at).toLocaleDateString()}`
                        : `Created ${new Date(invoice.created_at).toLocaleDateString()}`
                      }
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-heading font-bold text-[#0B1F3A]">${invoice.total?.toFixed(2)}</div>
                  </div>
                  {statusBadge(invoice.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Profile Tab ────────────────────────────────────────────
const ProfileTab = ({ user, API, headers }) => {
  const [form, setForm] = useState({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, form, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6 animate-fadeIn">
      <h2 className="font-heading text-2xl font-bold text-[#0B1F3A]">Profile Settings</h2>
      <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-card">
        <div className="flex items-center gap-4 mb-7">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-2xl text-white" style={{ background: '#0B1F3A' }}>
            {user?.first_name?.charAt(0)}
          </div>
          <div>
            <div className="font-heading font-bold text-[#0B1F3A] text-lg">{user?.first_name} {user?.last_name}</div>
            <div className="text-[#475569] text-sm">{user?.email}</div>
            <div className="text-xs text-[#94A3B8]">Client since {new Date(user?.created_at).getFullYear()}</div>
          </div>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">First Name</label>
              <input value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="profile-first-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Last Name</label>
              <input value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="profile-last-name" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Email</label>
            <input value={user?.email} disabled className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-400 cursor-not-allowed" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Phone</label>
            <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full h-11 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all" data-testid="profile-phone" />
          </div>
          <button type="submit" disabled={saving} className="w-full h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg disabled:opacity-60" style={{ background: '#0B1F3A' }} data-testid="save-profile-btn">
            {saving ? <Loader size={16} className="animate-spin" /> : null}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClientPortal;
